import { useCallback, useState } from 'react';
import type React from 'react';

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { useScrollTracking } from '#src/app/pages/petitionDetails/shared/hooks';
import type { WaypointCallbackArgs } from '#src/app/shared/components/layout/lazy';
import { Waypoint } from '#src/app/shared/components/scroll';

type Props = Readonly<{
	name: string;
	trackingData?: TrackingProperties;
	/**
	 * Forwarded to the underlying `Waypoint`'s `bottomOffset` (see its own doc for the type
	 * semantics). Use this to account for UI that visually covers the bottom of the viewport (e.g. a
	 * sticky action bar) so a "seen" event doesn't fire for a section that's occluded, not visible.
	 */
	bottomOffset?: number | string;
}>;

export const ScrollTrackingWaypoint: React.FC<Props> = ({ name, trackingData, bottomOffset }) => {
	const [tracked, setTracked] = useState(false);
	const trackScroll = useScrollTracking();

	const onPositionChange = useCallback(
		({ currentPosition }: WaypointCallbackArgs) => {
			if (tracked || currentPosition === 'below') {
				return;
			}
			trackScroll(name, trackingData);
			setTracked(true);
		},

		// trackingData is not memoized and will trigger recreation
		// of this function if added as a dependency
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[name, tracked, setTracked, trackScroll],
	);

	return <Waypoint onPositionChange={onPositionChange} bottomOffset={bottomOffset} />;
};
